import { ComponentProps, useCallback, useId, useMemo } from 'react'

import {
  EmbroideryThread,
  ColorSelector as LMFColorSelector,
  Warning,
} from '@syconium/little-miss-figgy'

import { EmbroideryColor } from '../../../../../../containers/EmbroideryContainer/useEmbroideryDesigns'
import { useRestrictedEmbroideryColors } from '../../../../../../containers/EmbroideryContainer/utils/isEmbroideryColorAllowedForProduct'
import { useEmbroideryColorFeedback } from '../../../../useEmbroideryColorFeedback'
import { ErrorMessage, Label, SelectedOptionText, WarningMessage } from '../../shared-form-styles'

export const ColorSelector: React.FC<{
  disabled?: boolean
  errorMessage?: string
  onSelectColor(color: EmbroideryColor): void
  selectedColor: EmbroideryColor | undefined
  label: string
  colorRestrictions: { key: string }[] | null | undefined
  productColorHex: string | undefined
}> = ({
  disabled,
  errorMessage,
  onSelectColor: onSelectColorProp,
  selectedColor,
  label,
  colorRestrictions,
  productColorHex,
}) => {
  const labelId = useId()
  const errorId = useId()
  const textColors = useRestrictedEmbroideryColors(colorRestrictions)

  const colorFeedback = useEmbroideryColorFeedback({
    backgroundColor: productColorHex,
    threadColor: selectedColor?.hexCode,
    warnWhenThreadColorMissing: true,
    praiseWhenColorsDiffer: true,
  })

  const colors: ComponentProps<typeof LMFColorSelector>['colors'] = useMemo(() => {
    return textColors.map(textColor => {
      return {
        availability: '',
        hexCodes: [textColor.hexCode],
        name: textColor.displayName,
        rawName: textColor.key,
      }
    })
  }, [textColors])

  const onSelectColor: ComponentProps<typeof LMFColorSelector>['onSelectColor'] = useCallback(
    (newColor: Parameters<ComponentProps<typeof LMFColorSelector>['onSelectColor']>[0]) => {
      const colorToPublish = textColors.find(textColor => textColor.key === newColor.rawName)
      if (colorToPublish) onSelectColorProp(colorToPublish)
    },
    [onSelectColorProp, textColors]
  )

  return (
    <div>
      <Label disabled={disabled ?? false} id={labelId}>
        {label}{' '}
        <SelectedOptionText disabled={disabled ?? false}>
          {selectedColor?.displayName}
        </SelectedOptionText>
      </Label>

      <LMFColorSelector
        aria-labelledby={labelId}
        aria-describedby={errorMessage ? errorId : undefined}
        colors={colors}
        disabled={disabled ?? false}
        disableMobileHorizontalSwipe
        onSelectColor={onSelectColor}
        selectedColorKey={selectedColor?.key}
      />

      {errorMessage ? (
        <ErrorMessage id={errorId} role='alert'>
          {errorMessage}
        </ErrorMessage>
      ) : colorFeedback ? (
        <WarningMessage
          role={colorFeedback.tone === 'caution' ? 'alert' : 'status'}
          $tone={colorFeedback.tone}
        >
          {colorFeedback.icon === 'thread' ? (
            <EmbroideryThread height={'13px'} width={'13px'} />
          ) : (
            <Warning height={'13px'} width={'13px'} fill={'currentcolor'} />
          )}
          {colorFeedback.message}
        </WarningMessage>
      ) : null}
    </div>
  )
}
